import React, { Component, Fragment } from 'react';

import { Box, Callout, Flex, Heading, Text } from 'core/components';
import { Field, RadioGroup, Select, formConsumer } from 'core/form';
import { inject, observer } from 'mobx-react';
import { QueryFiltersObject } from '@kentik/ui-shared/types/queries';

type DimensionsObj = Record<string, { QueryColumn?: string }>;

interface MetricsExplorerGuidedModeBehaviorProps {
  form: {
    getField: (name: string) => {
      setOptions: (options?: string[]) => void;
      setRules: (rules: string) => void;
      getValue: <T>() => T | undefined;
      setValue: (value: string) => void;
    };
    getValue: <T>(name: string) => T;
    getValues: <T extends Record<string, unknown>>() => T;
    setValue: (name: string, value: unknown) => void;
  };
  availableFiltersConfig: {
    availableFilters: { group: string; value: string; label: string }[];
    canOverrideSpecific: boolean;
  };
  parametricMode: string;
  filterOperatorOptions: Record<string, { label: string; value: string }[]>;
  standardFilterOperators: { label: string; value: string }[];
  disabled?: boolean;
  $metrics: {
    measurementModel: (measurement: string) => {
      get: (field: string) => unknown;
      storage: {
        Dimensions: DimensionsObj;
      };
    };
  };
  parametricType: { type: string };
  measurement?: string;
  filterFields?: QueryFiltersObject;
}

function getGuidedModeDimensions(
  { type = '' }: { type: string },
  _dimensionList: DimensionsObj
): {
  label: string;
  value: string;
}[] {
  if (type === 'site') {
    return [{ label: 'Site Name', value: 'i_device_site_name' }];
  }

  if (type === 'device') {
    return [{ label: 'Device Name', value: 'i_device_name' }];
  }

  if (type === 'country') {
    return [{ label: 'Country', value: 'i_device_site_country' }];
  }

  // TODO: Once we're enhancing our dimensions with this parametricType field, we can filter validDimensions here

  return [];
}

type MetricsExplorerGuidedModeBehaviorState = {
  measurementDimensions: Record<string, { QueryColumn?: string }> | null;
  guidedModeDimensions: { label: string; value: string }[] | null;
};

@inject('$metrics')
// @ts-expect-error - need to fix formConsumer
@formConsumer
@observer
export default class MetricsExplorerGuidedModeBehavior extends Component<
  MetricsExplorerGuidedModeBehaviorProps,
  MetricsExplorerGuidedModeBehaviorState
> {
  state: MetricsExplorerGuidedModeBehaviorState = {
    measurementDimensions: null,
    guidedModeDimensions: null
  };

  /**
   * Reset form fields to "ignore" state - clears options and validation rules
   */
  resetParametricFields = () => {
    const { form } = this.props;
    form.getField('parametric_overrides.filterField').setOptions();
    form.getField('parametric_overrides.operator').setOptions();
    form.getField('parametric_overrides.filterField').setRules('');
    form.getField('parametric_overrides.operator').setRules('');
  };

  /**
   * Set form fields to require validation
   */
  setParametricFieldsRequired = () => {
    const { form } = this.props;
    form.getField('parametric_overrides.filterField').setRules('required');
    form.getField('parametric_overrides.operator').setRules('required');
  };

  onParametricModeChange = (_: string, value: string) => {
    if (value === 'ignore') {
      this.resetParametricFields();
    } else {
      this.setParametricFieldsRequired();
    }
  };

  onParametricFieldChange = (_: string, value: string) => {
    const { form, filterOperatorOptions, standardFilterOperators } = this.props;
    const operators = filterOperatorOptions?.[value] ?? standardFilterOperators;
    const parametricOperatorField = form?.getField?.('parametric_overrides.operator');
    const parametricOperator = parametricOperatorField?.getValue<string>();

    // Set operator as first available option if current value is not valid
    if (parametricOperator && !(parametricOperator in operators)) {
      parametricOperatorField?.setValue(Object.keys(filterOperatorOptions)[0]);
    }
  };

  private getMeasurementDimensions = (measurement?: string) => {
    const { $metrics } = this.props;
    if (!measurement) {
      return null;
    }

    const measurementWithMetadata = $metrics.measurementModel(measurement);
    return measurementWithMetadata?.storage?.Dimensions ?? null;
  };

  private getGuidedModeDimensions = (
    parametricType: MetricsExplorerGuidedModeBehaviorProps['parametricType'] | undefined,
    measurementDimensions: DimensionsObj | null
  ) => {
    if (!parametricType || !measurementDimensions) {
      return null;
    }

    return getGuidedModeDimensions(parametricType, measurementDimensions);
  };

  private syncFromProps = (prevProps?: Readonly<MetricsExplorerGuidedModeBehaviorProps>) => {
    const { measurement, parametricType, form } = this.props;
    const { measurementDimensions } = this.state;
    const measurementChanged = !prevProps || measurement !== prevProps.measurement;
    const parametricTypeChanged = !prevProps || parametricType?.type !== prevProps.parametricType?.type;

    if (!measurementChanged && !parametricTypeChanged) {
      return;
    }

    const currentMeasurementDimensions = measurementChanged
      ? this.getMeasurementDimensions(measurement)
      : measurementDimensions;

    const guidedModeDimensions = this.getGuidedModeDimensions(parametricType, currentMeasurementDimensions);

    // If there are no valid dimensions for guided mode, reset parametric mode to "ignore" and clear fields
    // Do not want to require valid parametric field/operator if there are no valid options to select from
    if (guidedModeDimensions && guidedModeDimensions.length === 0) {
      form.setValue('parametric_mode', 'ignore');
      this.resetParametricFields();
    }

    this.setState((prevState) => ({
      ...prevState,
      measurementDimensions: currentMeasurementDimensions,
      guidedModeDimensions
    }));
  };

  componentDidMount() {
    this.syncFromProps();
  }

  componentDidUpdate(prevProps: Readonly<MetricsExplorerGuidedModeBehaviorProps>) {
    this.syncFromProps(prevProps);
  }

  render() {
    const {
      availableFiltersConfig,
      parametricMode,
      filterOperatorOptions,
      standardFilterOperators,
      disabled = false,
      measurement,
      form,
      filterFields
    } = this.props;

    const { guidedModeDimensions } = this.state;

    // Default to first available filter if current value is not valid - this can happen when switching measurements or parametric types and the previously selected filter field is not available
    const selectedParametricFilter =
      form.getValue<string>('parametric_overrides.filterField') || availableFiltersConfig.availableFilters[0]?.value;

    const parametricOperatorOptions = filterOperatorOptions[selectedParametricFilter] || standardFilterOperators;

    const shouldRenderOperatorField = parametricMode === 'override_all' || parametricMode === 'add_group';
    const hasOptions = Array.isArray(guidedModeDimensions) && guidedModeDimensions.length > 0;

    const { measurementDimensions } = this.state;

    const activeQueryFilters = filterFields?.filterGroups?.flatMap(
      (filterGroup) =>
        filterGroup?.filters?.map(
          (filter) => measurementDimensions?.[filter.filterField]?.QueryColumn || filter.filterField
        ) || []
    );

    const hasOverrideSpecificOption =
      guidedModeDimensions?.some?.((dimension) => activeQueryFilters?.includes(dimension.value)) ?? false;

    return (
      <Flex flexDirection="column" className="overflow-auto" flexAuto>
        <Heading level={6}>Panel Guided Mode Behavior</Heading>

        <Text muted>Select the effect you want the user&apos;s input to have on this panel</Text>
        <Box>
          <Field
            name="parametric_mode"
            className="no-margin"
            onChange={this.onParametricModeChange}
            labelStyle={{ textTransform: 'none' }}
            showLabel={false}
            disabled={!measurement || !hasOptions || disabled}
          >
            <RadioGroup inline={false} disabledOptions={hasOverrideSpecificOption ? [] : ['override_specific']} />
          </Field>
        </Box>

        {measurement && !hasOptions && (
          <Callout p={2} mt={2} intent="warning" title="No Valid Guided Mode Dimensions">
            This measurement does not have any valid dimensions for filtering based on the selected Guided Mode
            category. This panel will ignore any Guided Mode input.
          </Callout>
        )}

        {!measurement && (
          <Callout p={2} mt={2} intent="danger" title="Missing Valid Query">
            Please select a valid query for this dashboard item before configuring the field and/or operator.
          </Callout>
        )}

        {parametricMode !== 'ignore' && measurement && (
          <Callout p={2} mt={4}>
            {parametricMode === 'override_all' && <p>Ignore panels existing filters and add a new filter group:</p>}
            {parametricMode === 'override_specific' && <p>Overriding all existing filters that contain:</p>}
            {parametricMode === 'add_group' && <p>Add a new filter group:</p>}
            <Fragment>
              <Field
                name="parametric_overrides.filterField"
                disabled={disabled}
                options={guidedModeDimensions}
                onChange={this.onParametricFieldChange}
              >
                <Select menuWidth={350} />
              </Field>

              {shouldRenderOperatorField && (
                <Field
                  name="parametric_overrides.operator"
                  className="no-margin"
                  disabled={disabled}
                  options={parametricOperatorOptions}
                >
                  <Select menuWidth={350} />
                </Field>
              )}
            </Fragment>
          </Callout>
        )}
      </Flex>
    );
  }
}
